import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../Navbar';
import ManagerRequisitions from './ManagerRequisitions';
import ManagerExits from './ManagerExits';
import DataImport from './DataImport';
import { DashboardOverviewSkeleton } from '../common/Skeleton';
import { API_BASE } from '../../utils/config';

// Helper API caller
const apiCall = async (url, options = {}) => {
  let user = {};
  try {
    user = JSON.parse(localStorage.getItem('user') || '{}');
  } catch (e) {
    user = {};
  }
  const hostname = window.location.hostname;
  const parts = hostname.split('.');
  const tenant = (parts.length >= 3) ? parts[0] : (parts.length === 2 && parts[1] === 'localhost') ? parts[0] : '';
  const token = user.token || localStorage.getItem('token') || '';
  const headers = {
    'Content-Type': 'application/json',
    'x-user-id': user.id || '',
    'x-user-role': user.user_type || user.role || 'MANAGER',
    'X-Tenant-ID': tenant,
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    ...options.headers
  };
  let baseUrl = API_BASE.endsWith('/') ? API_BASE.slice(0, -1) : API_BASE;
  if (typeof window !== 'undefined' && window.location.protocol === 'https:' && baseUrl.startsWith('http://')) {
    baseUrl = baseUrl.replace('http://', 'https://');
  }
  const res = await fetch(`${baseUrl}${url}`, { ...options, headers });
  if (!res.ok) throw new Error(`HTTP error ${res.status}`);
  return res.json();
};

// Authority tier configuration & metadata
const getAuthorityMeta = (level) => {
  switch (level) {
    case 'L1':
      return {
        level: 'L1',
        icon: '⚡',
        label: 'L1 Tech Evaluator',
        shortLabel: 'L1 Tech',
        fullTitle: 'Round 1: Coding & Syntax Assessor',
        desc: 'Certified for coding tests, syntax, and data structures.',
        badgeBg: 'rgba(79, 70, 229, 0.08)',
        badgeColor: '#4f46e5',
        badgeBorder: 'rgba(79, 70, 229, 0.25)',
        avatarBg: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
        accentColor: '#4f46e5'
      };
    case 'L2':
      return {
        level: 'L2',
        icon: '🚀',
        label: 'L2 Domain Specialist',
        shortLabel: 'L2 Domain',
        fullTitle: 'Round 2: Architecture & System Design',
        desc: 'Certified for architecture, domain challenges & framework internals.',
        badgeBg: 'rgba(124, 58, 237, 0.08)',
        badgeColor: '#7c3aed',
        badgeBorder: 'rgba(124, 58, 237, 0.25)',
        avatarBg: 'linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)',
        accentColor: '#7c3aed'
      };
    case 'L3':
      return {
        level: 'L3',
        icon: '👑',
        label: 'L3 Bar Raiser & Lead',
        shortLabel: 'L3 Lead',
        fullTitle: 'Round 3: Behavioral & Final Fit Assessor',
        desc: 'Certified for managerial, bar-raising, and hiring recommendations.',
        badgeBg: 'rgba(5, 150, 105, 0.08)',
        badgeColor: '#059669',
        badgeBorder: 'rgba(5, 150, 105, 0.25)',
        avatarBg: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        accentColor: '#059669'
      };
    case 'NONE':
    default:
      return {
        level: 'NONE',
        icon: '🛡️',
        label: 'Exempt Engineer',
        shortLabel: 'Exempt',
        fullTitle: 'Non-Interviewer (Exempted from Interviews)',
        desc: 'Exempt from interview scheduling. Focused on sprint deliverables.',
        badgeBg: 'rgba(100, 116, 139, 0.08)',
        badgeColor: '#64748b',
        badgeBorder: 'rgba(100, 116, 139, 0.2)',
        avatarBg: 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)',
        accentColor: '#64748b'
      };
  }
};

const getWorkloadChip = (emp) => {
  const isExempt = !emp.interviewer_level || emp.interviewer_level === 'NONE';
  if (isExempt) {
    return (
      <span style={{
        fontSize: '10.5px',
        fontWeight: '700',
        padding: '3px 8px',
        borderRadius: '6px',
        backgroundColor: '#f1f5f9',
        color: '#64748b',
        border: '1px solid #e2e8f0',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px'
      }}>
        🛡️ Exempt
      </span>
    );
  }

  const scheduled = emp.scheduled_count || 0;
  if (scheduled >= 3) {
    return (
      <span style={{
        fontSize: '10.5px',
        fontWeight: '800',
        padding: '3px 8px',
        borderRadius: '6px',
        backgroundColor: '#fee2e2',
        color: '#b91c1c',
        border: '1px solid #fca5a5',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px'
      }}>
        🔥 HIGH LOAD ({scheduled})
      </span>
    );
  }

  if (scheduled > 0) {
    return (
      <span style={{
        fontSize: '10.5px',
        fontWeight: '700',
        padding: '3px 8px',
        borderRadius: '6px',
        backgroundColor: '#fef3c7',
        color: '#b45309',
        border: '1px solid #fcd34d',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px'
      }}>
        ⚖️ BALANCED ({scheduled})
      </span>
    );
  }

  return (
    <span style={{
      fontSize: '10.5px',
      fontWeight: '700',
      padding: '3px 8px',
      borderRadius: '6px',
      backgroundColor: '#dcfce7',
      color: '#15803d',
      border: '1px solid #86efac',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px'
    }}>
      ✨ AVAILABLE
    </span>
  );
};

export default function ManagerDashboard() {
  const { subTab } = useParams();
  const navigate = useNavigate();

  const urlToStateMap = {
    overview: 'overview',
    roles: 'roles',
    hiring: 'requisitions',
    exits: 'exits',
    onboard: 'onboard'
  };

  const stateToUrlMap = {
    overview: '',
    roles: 'roles',
    requisitions: 'hiring',
    exits: 'exits',
    onboard: 'onboard'
  };

  const [activeTab, setActiveTab] = useState(urlToStateMap[subTab] || 'overview');
  const [loading, setLoading] = useState(true);
  const [projectData, setProjectData] = useState({
    project: { id: null, name: 'Loading...' },
    headcount: 0,
    employees: []
  });

  const [editingEmployee, setEditingEmployee] = useState(null);
  const [roleInput, setRoleInput] = useState('');
  const [respInput, setRespInput] = useState('');
  const [authorityInput, setAuthorityInput] = useState('NONE');
  const [isUpdating, setIsUpdating] = useState(false);

  // Search, filter, view mode, and toast
  const [searchQuery, setSearchQuery] = useState('');
  const [authorityFilter, setAuthorityFilter] = useState('ALL');
  const [viewMode, setViewMode] = useState('grid');
  const [toastMessage, setToastMessage] = useState(null);
  const [quickUpdatingId, setQuickUpdatingId] = useState(null);

  let managerUser = {};
  try {
    managerUser = JSON.parse(localStorage.getItem('user') || '{}');
  } catch (e) {
    managerUser = {};
  }

  useEffect(() => {
    // Clean up auth_data query parameter from URL bar if present
    if (typeof window !== 'undefined' && window.location.search.includes('auth_data')) {
      const cleanSearch = window.location.search.replace(/[?&]auth_data=[^&]+/, '').replace(/^&/, '?').replace(/\?$/, '');
      const newUrl = window.location.pathname + cleanSearch;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, []);

  useEffect(() => {
    if (toastMessage) {
      const timer = setTimeout(() => setToastMessage(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [toastMessage]);

  const handleEditRoleClick = (emp) => {
    setEditingEmployee(emp);
    setRoleInput(emp.role || 'Software Engineer');
    setRespInput(emp.responsibilities || 'Core product engineering and task execution');
    setAuthorityInput(emp.interviewer_level || 'NONE');
  };

  const handleUpdateRoleSubmit = async (e) => {
    e.preventDefault();
    if (!editingEmployee) return;

    try {
      setIsUpdating(true);
      await apiCall(`/api/employees/${editingEmployee.id}/role-details`, {
        method: 'PUT',
        body: JSON.stringify({
          role: roleInput,
          responsibilities: respInput,
          interviewer_level: authorityInput
        })
      });
      setToastMessage({
        type: 'success',
        text: `Successfully updated ${editingEmployee.full_name}'s role and authority specifications.`
      });
      setEditingEmployee(null);
      fetchProjectDetails(); // Refresh project data
    } catch (err) {
      console.error('Failed to update employee role:', err);
      setToastMessage({ type: 'error', text: 'Failed to update employee role details.' });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleQuickAuthorityChange = async (emp, newLevel) => {
    try {
      setQuickUpdatingId(emp.id);
      // Optimistic update in UI
      setProjectData(prev => ({
        ...prev,
        employees: (prev.employees || []).map(e => e.id === emp.id ? { ...e, interviewer_level: newLevel } : e)
      }));

      await apiCall(`/api/employees/${emp.id}/interviewer-authority`, {
        method: 'PUT',
        body: JSON.stringify({ interviewer_level: newLevel })
      });

      const levelLabels = {
        NONE: 'Exempt (Non-Interviewer)',
        L1: 'L1 Technical Evaluator',
        L2: 'L2 Domain Specialist',
        L3: 'L3 Bar Raiser & Lead'
      };

      setToastMessage({
        type: 'success',
        text: `${emp.full_name} is now designated as ${levelLabels[newLevel] || newLevel}. ${newLevel !== 'NONE' ? 'Eligible for HR candidate scheduling!' : 'Exempted from interviews.'}`
      });

      fetchProjectDetails();
    } catch (err) {
      console.error('Failed to update authority:', err);
      setToastMessage({ type: 'error', text: 'Failed to update interviewer authority.' });
      fetchProjectDetails();
    } finally {
      setQuickUpdatingId(null);
    }
  };

  const fetchProjectDetails = async () => {
    try {
      setLoading(true);
      const data = await apiCall('/api/employees/project-details');
      setProjectData(data);
    } catch (err) {
      console.error('Failed to load project details:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjectDetails();
  }, []);

  useEffect(() => {
    const targetTab = urlToStateMap[subTab] || 'overview';
    if (activeTab !== targetTab) {
      setActiveTab(targetTab);
    }
  }, [subTab]);

  const handleTabChange = (newTab) => {
    const urlParam = stateToUrlMap[newTab];
    if (urlParam) {
      navigate(`/manager-dashboard/${urlParam}`);
    } else {
      navigate('/manager-dashboard');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    navigate('/login');
  };

  const tabs = [
    {
      id: 'overview',
      label: 'Overview Dashboard',
      icon: (
        <svg style={styles.tabIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      )
    },
    {
      id: 'roles',
      label: 'Team Management',
      icon: (
        <svg style={styles.tabIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      )
    },
    {
      id: 'requisitions',
      label: 'Hiring',
      icon: (
        <svg style={styles.tabIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      )
    },
    {
      id: 'exits',
      label: 'Offboarding',
      icon: (
        <svg style={styles.tabIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
        </svg>
      )
    },
    {
      id: 'onboard',
      label: 'Onboard Team',
      icon: (
        <svg style={styles.tabIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
        </svg>
      )
    }
  ];

  const renderContent = () => {
    if (loading) {
      return <DashboardOverviewSkeleton statsCount={4} showGrid={true} />;
    }

    switch (activeTab) {
      case 'overview': {
        const employees = projectData?.employees || [];
        const activeEmployees = employees.filter(emp => emp.exit_status === 'ACTIVE');
        const leaverEmployees = employees.filter(emp => emp.exit_status !== 'ACTIVE');
        const totalEmployees = employees.length;
        const stabilityIndex = totalEmployees > 0 ? ((activeEmployees.length / totalEmployees) * 100).toFixed(0) : 100;
        const hasProject = Boolean(projectData?.project?.id);

        return (
          <div style={styles.overviewContainer}>
            {/* Project Title Block */}
            <div style={styles.cardHeader}>
              <div style={styles.cardHeaderLayout}>
                <div>
                  <h2 style={styles.sectionTitle}>
                    Project Overview: {hasProject ? projectData.project.name : (projectData.project?.name && projectData.project.name !== 'No Assigned Project' ? projectData.project.name : 'General Overview')}
                  </h2>
                  <p style={styles.sectionSubtitle}>
                    {hasProject 
                      ? (projectData.project.description || 'No description provided.')
                      : 'Your manager account is not currently assigned to an active project. Onboard your team or request HR to assign a project.'}
                  </p>
                </div>
                <span style={hasProject ? styles.projectActiveBadge : styles.projectPendingBadge}>
                  {hasProject ? 'RUNNING' : 'SETUP PENDING'}
                </span>
              </div>
            </div>

            {/* Informational Onboarding Notice if not assigned */}
            {!hasProject && (
              <div style={styles.infoBanner}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '24px' }}>💡</span>
                  <div>
                    <div style={{ fontWeight: '600', fontSize: '14px', color: 'var(--text-main, #0f172a)' }}>
                      No Team Onboarded Yet
                    </div>
                    <div style={{ fontSize: '13px', color: 'var(--text-muted, #64748b)' }}>
                      Your manager account is not currently assigned to an active project. You can onboard your team roster or request HR Admin to import your project.
                    </div>
                  </div>
                </div>
                <button 
                  style={styles.btnBannerAction}
                  onClick={() => handleTabChange('onboard')}
                >
                  Onboard Team
                </button>
              </div>
            )}

            {/* Metrics Row */}
            <div style={styles.statsRow}>
              {/* Active Headcount */}
              <div style={styles.statCard}>
                <div style={styles.statIconContainerIndigo}>
                  <svg style={styles.statIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <div>
                  <h3 style={styles.statLabel}>Active Headcount</h3>
                  <p style={styles.statValue}>{activeEmployees.length}</p>
                  <span style={styles.statSubtext}>Currently active members</span>
                </div>
              </div>

              {/* Exiting Count */}
              <div style={styles.statCard}>
                <div style={styles.statIconContainerRed}>
                  <svg style={styles.statIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                </div>
                <div>
                  <h3 style={styles.statLabel}>Leaver Employees</h3>
                  <p style={styles.statValueRed}>{leaverEmployees.length}</p>
                  <span style={styles.statSubtext}>Clearances pending / approved</span>
                </div>
              </div>

              {/* Project Specifications */}
              <div style={styles.statCard}>
                <div style={styles.statIconContainerTeal}>
                  <svg style={styles.statIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                  </svg>
                </div>
                <div>
                  <h3 style={styles.statLabel}>Project Specifications</h3>
                  <p style={styles.statValueTeal}>{hasProject ? `ID #${projectData.project.id}` : 'Unassigned'}</p>
                  <span style={styles.statSubtext}>{hasProject ? (projectData.project?.name || 'Apollo Engineering Pool') : 'No Active Pool'}</span>
                </div>
              </div>
            </div>

            {/* Team Stability & Composition (Visual Progress) */}
            <div style={styles.stabilityCard}>
              <div style={styles.stabilityHeader}>
                <div>
                  <h3 style={styles.stabilityTitle}>Team Stability & Composition</h3>
                  <p style={styles.stabilitySubtitle}>Roster retention index and exit clearance status breakdown.</p>
                </div>
                <div style={styles.stabilityScoreCard}>
                  <span style={styles.stabilityScore}>{stabilityIndex}%</span>
                  <span style={styles.stabilityScoreLabel}>Stability Index</span>
                </div>
              </div>
              <div style={styles.stabilityBarBg}>
                <div style={{ ...styles.stabilityBarFillActive, width: `${stabilityIndex}%` }} />
                <div style={{ ...styles.stabilityBarFillExit, width: `${totalEmployees > 0 ? (leaverEmployees.length / totalEmployees) * 100 : 0}%` }} />
              </div>
              <div style={styles.stabilityLegend}>
                <div style={styles.legendItem}>
                  <div style={styles.legendDotActive} />
                  <span>Active Members ({activeEmployees.length})</span>
                </div>
                <div style={styles.legendItem}>
                  <div style={styles.legendDotExit} />
                  <span>Exiting/Leavers ({leaverEmployees.length})</span>
                </div>
              </div>
            </div>

            {/* Candidate Recruitment Process */}
            {projectData.screeningFunnel && (
              <div style={styles.funnelCard}>
                <h3 style={styles.stabilityTitle}>Recruitment & Screening Process</h3>
                <p style={styles.stabilitySubtitle}>Visual tracking of screened candidates for active project positions.</p>
                <div style={styles.funnelStages}>
                  {/* Screened */}
                  <div style={styles.funnelStage}>
                    <div style={styles.funnelIconBlue}>📄</div>
                    <div style={styles.funnelStageVal}>{projectData.screeningFunnel.screened}</div>
                    <div style={styles.funnelStageLabel}>Screened</div>
                  </div>
                  <div style={styles.funnelArrow}>➔</div>

                  {/* Shortlisted */}
                  <div style={styles.funnelStage}>
                    <div style={styles.funnelIconPurple}>🎯</div>
                    <div style={styles.funnelStageVal}>{projectData.screeningFunnel.shortlisted}</div>
                    <div style={styles.funnelStageLabel}>Shortlisted</div>
                  </div>
                  <div style={styles.funnelArrow}>➔</div>

                  {/* Scheduled */}
                  <div style={styles.funnelStage}>
                    <div style={styles.funnelIconYellow}>🗓️</div>
                    <div style={styles.funnelStageVal}>{projectData.screeningFunnel.scheduled}</div>
                    <div style={styles.funnelStageLabel}>Scheduled</div>
                  </div>
                  <div style={styles.funnelArrow}>➔</div>

                  {/* Interviewed */}
                  <div style={styles.funnelStage}>
                    <div style={styles.funnelIconTeal}>👥</div>
                    <div style={styles.funnelStageVal}>{projectData.screeningFunnel.interviewed}</div>
                    <div style={styles.funnelStageLabel}>Interviewed</div>
                  </div>
                  <div style={styles.funnelArrow}>➔</div>

                  {/* Selected / Cleared */}
                  <div style={styles.funnelStage}>
                    <div style={styles.funnelIconGreen}>🏆</div>
                    <div style={styles.funnelStageVal}>{projectData.screeningFunnel.cleared}</div>
                    <div style={styles.funnelStageLabel}>Selected</div>
                  </div>
                </div>
              </div>
            )}

            {/* Split Logs Layout */}
            <div style={styles.rosterGrid}>
              {/* Active Team Roster */}
              <div style={styles.tableCard}>
                <div style={styles.tableCardHeaderBlock}>
                  <h3 style={styles.tableTitle}>Active Team Roster</h3>
                  <p style={styles.tableSubtitle}>List of current project members and interviewer workload status.</p>
                </div>
                {activeEmployees.length === 0 ? (
                  <div style={styles.emptyText}>No active employees in this project.</div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={styles.table}>
                      <thead>
                        <tr style={styles.trHeader}>
                          <th style={styles.th}>Name</th>
                          <th style={styles.th}>Role / Level</th>
                          <th style={styles.th}>Interviews (Active/Done)</th>
                          <th style={styles.th}>Workload Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activeEmployees.map(emp => {
                          const isHighLoad = (emp.scheduled_count || 0) >= 3;
                          const hasInterviewerLevel = emp.interviewer_level && emp.interviewer_level !== 'NONE';
                          
                          return (
                            <tr key={emp.id} style={styles.tr}>
                              <td style={styles.tdName}>
                                <div>
                                  <div style={{ fontWeight: '600' }}>{emp.full_name}</div>
                                  <div style={{ fontSize: '11px', color: 'var(--text-muted, #64748b)' }}>{emp.email}</div>
                                </div>
                              </td>
                              <td style={styles.td}>
                                <div>
                                  <div style={{ fontWeight: '500', fontSize: '13px' }}>{emp.role || 'Software Engineer'}</div>
                                  {hasInterviewerLevel ? (
                                    <span style={{
                                      fontSize: '10px',
                                      fontWeight: '800',
                                      backgroundColor: 'rgba(79, 70, 229, 0.08)',
                                      color: 'var(--accent-primary, #4f46e5)',
                                      padding: '1px 5px',
                                      borderRadius: '4px',
                                      display: 'inline-block',
                                      marginTop: '2px'
                                    }}>
                                      ⚡ {emp.interviewer_level} Assessor
                                    </span>
                                  ) : (
                                    <span style={{ fontSize: '10px', color: '#94a3b8' }}>
                                      Non-Interviewer
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td style={styles.td}>
                                <div style={{ fontSize: '13px' }}>
                                  <strong>{emp.scheduled_count || 0}</strong> Active / <strong>{emp.completed_count || 0}</strong> Done
                                </div>
                              </td>
                              <td style={styles.td}>
                                {hasInterviewerLevel ? (
                                  isHighLoad ? (
                                    <span style={{
                                      backgroundColor: '#fee2e2',
                                      color: '#ef4444',
                                      padding: '4px 10px',
                                      borderRadius: '6px',
                                      fontSize: '11px',
                                      fontWeight: 'bold',
                                      display: 'inline-block'
                                    }}>
                                      🔥 HIGH LOAD
                                    </span>
                                  ) : (
                                    <span style={{
                                      backgroundColor: '#dcfce7',
                                      color: '#15803d',
                                      padding: '4px 10px',
                                      borderRadius: '6px',
                                      fontSize: '11px',
                                      fontWeight: 'bold',
                                      display: 'inline-block'
                                    }}>
                                      ⚖️ BALANCED
                                    </span>
                                  )
                                ) : (
                                  <span style={{ color: '#64748b', fontSize: '11px', fontWeight: '600' }}>
                                    🛡️ Exempt
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Exiting Team Logs */}
              <div style={styles.tableCard}>
                <div style={styles.tableCardHeaderBlock}>
                  <h3 style={styles.tableTitle}>Exiting Team Logs</h3>
                  <p style={styles.tableSubtitle}>Pending exits, clearance track and last working days.</p>
                </div>
                {leaverEmployees.length === 0 ? (
                  <div style={styles.emptyText}>No exit registrations logged for this project.</div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={styles.table}>
                      <thead>
                        <tr style={styles.trHeader}>
                          <th style={styles.th}>Employee Name</th>
                          <th style={styles.th}>LWD (Timeline)</th>
                          <th style={styles.th}>Exit Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {leaverEmployees.map(emp => (
                          <tr key={emp.id} style={styles.tr}>
                            <td style={styles.tdTitle}>
                              <div>
                                <div style={{ fontWeight: '600' }}>{emp.full_name}</div>
                                <div style={{ fontSize: '11px', color: 'var(--text-muted, #64748b)' }}>{emp.email}</div>
                              </div>
                            </td>
                            <td style={styles.td}>
                              {emp.last_working_day ? new Date(emp.last_working_day).toLocaleDateString() : 'N/A'}
                            </td>
                            <td style={styles.td}>
                              <span style={emp.exit_status === 'EXIT_APPROVED' ? styles.badgeApproved : styles.badgePending}>
                                {emp.exit_status === 'EXIT_APPROVED' ? 'APPROVED' : 'PENDING'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      }
      case 'roles': {
        const employees = projectData?.employees || [];
        const activeEmployees = employees.filter(emp => emp.exit_status === 'ACTIVE');

        // Metric calculations
        const l1Count = activeEmployees.filter(emp => emp.interviewer_level === 'L1').length;
        const l2Count = activeEmployees.filter(emp => emp.interviewer_level === 'L2').length;
        const l3Count = activeEmployees.filter(emp => emp.interviewer_level === 'L3').length;
        const authorizedCount = l1Count + l2Count + l3Count;
        const exemptCount = activeEmployees.filter(emp => !emp.interviewer_level || emp.interviewer_level === 'NONE').length;
        const totalScheduled = activeEmployees.reduce((acc, emp) => acc + (emp.scheduled_count || 0), 0);
        const totalCompleted = activeEmployees.reduce((acc, emp) => acc + (emp.completed_count || 0), 0);

        // Live search & filters
        const q = searchQuery.toLowerCase().trim();
        const filteredEmployees = activeEmployees.filter(emp => {
          const matchesSearch = !q || 
            (emp.full_name && emp.full_name.toLowerCase().includes(q)) ||
            (emp.email && emp.email.toLowerCase().includes(q)) ||
            (emp.role && emp.role.toLowerCase().includes(q));

          let matchesAuth = true;
          if (authorityFilter === 'AUTHORIZED') {
            matchesAuth = emp.interviewer_level && emp.interviewer_level !== 'NONE';
          } else if (authorityFilter === 'NONE') {
            matchesAuth = !emp.interviewer_level || emp.interviewer_level === 'NONE';
          } else if (authorityFilter === 'L1' || authorityFilter === 'L2' || authorityFilter === 'L3') {
            matchesAuth = emp.interviewer_level === authorityFilter;
          }

          return matchesSearch && matchesAuth;
        });

        // Quick role suggestions
        const rolePresets = [
          'Software Engineer',
          'Full Stack Developer',
          'Senior Backend Engineer',
          'Frontend Specialist',
          'Cloud Solutions Architect',
          'DevOps Engineer',
          'QA Automation Lead'
        ];

        return (
          <div style={styles.overviewContainer}>
            {/* Header Block */}
            <div style={styles.cardHeader}>
              <div style={styles.cardHeaderLayout}>
                <div>
                  <h2 style={styles.sectionTitle}>Team Management & Interviewer Authority</h2>
                  <p style={styles.sectionSubtitle}>
                    Define project engineering roles, designate certified interview assessors (L1, L2, L3), and govern interview bandwidth across your team.
                  </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={styles.projectActiveBadge}>
                    PROJECT #{projectData?.project?.id || 'ACTIVE'}
                  </span>
                </div>
              </div>
            </div>

            {/* 4 Executive Metric Overview Cards */}
            <div style={styles.statsRow}>
              {/* Card 1: Team Headcount */}
              <div style={styles.statCard}>
                <div style={styles.statIconContainerIndigo}>
                  <svg style={styles.statIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <div>
                  <h3 style={styles.statLabel}>Active Team Members</h3>
                  <p style={styles.statValue}>{activeEmployees.length}</p>
                  <span style={styles.statSubtext}>Total project engineers</span>
                </div>
              </div>

              {/* Card 2: Certified Interviewers */}
              <div style={styles.statCard}>
                <div style={styles.statIconContainerTeal}>
                  <svg style={styles.statIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138z" />
                  </svg>
                </div>
                <div>
                  <h3 style={styles.statLabel}>Certified Interviewers</h3>
                  <p style={styles.statValueTeal}>{authorizedCount}</p>
                  <span style={styles.statSubtext}>
                    {l1Count} L1 • {l2Count} L2 • {l3Count} L3
                  </span>
                </div>
              </div>

              {/* Card 3: Exempt Members */}
              <div style={styles.statCard}>
                <div style={{ ...styles.statIconContainerIndigo, backgroundColor: 'rgba(100, 116, 139, 0.1)', color: '#64748b' }}>
                  <svg style={styles.statIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <div>
                  <h3 style={styles.statLabel}>Exempt Engineers</h3>
                  <p style={{ ...styles.statValue, color: '#64748b' }}>{exemptCount}</p>
                  <span style={styles.statSubtext}>Exempted • 100% focused on sprint delivery</span>
                </div>
              </div>

              {/* Card 4: Assessor Workload */}
              <div style={styles.statCard}>
                <div style={styles.statIconContainerRed}>
                  <svg style={styles.statIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 style={styles.statLabel}>Assessor Workload</h3>
                  <p style={{ ...styles.statValue, color: '#f59e0b' }}>{totalScheduled} Active</p>
                  <span style={styles.statSubtext}>{totalCompleted} completed evaluations</span>
                </div>
              </div>
            </div>

            {/* Filter, Search & View Controls Bar */}
            <div style={styles.controlsBar}>
              {/* Search Box */}
              <div style={styles.searchBoxWrap}>
                <span style={styles.searchIconSymbol}>🔍</span>
                <input
                  type="text"
                  placeholder="Search by engineer name, email, or role title..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={styles.searchInputField}
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    style={styles.clearSearchBtn}
                    title="Clear search"
                  >
                    ✕
                  </button>
                )}
              </div>

              {/* Authority Filter Tabs */}
              <div style={styles.filterPillsGroup}>
                {[
                  { id: 'ALL', label: `All (${activeEmployees.length})` },
                  { id: 'AUTHORIZED', label: `⚡ Certified (${authorizedCount})` },
                  { id: 'L1', label: `L1 Tech (${l1Count})` },
                  { id: 'L2', label: `L2 Domain (${l2Count})` },
                  { id: 'L3', label: `L3 Lead (${l3Count})` },
                  { id: 'NONE', label: `🛡️ Exempt (${exemptCount})` }
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setAuthorityFilter(tab.id)}
                    style={{
                      ...styles.filterPillBtn,
                      ...(authorityFilter === tab.id ? styles.filterPillBtnActive : {})
                    }}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* View Switcher */}
              <div style={styles.viewModeToggleGroup}>
                <button
                  onClick={() => setViewMode('grid')}
                  style={{
                    ...styles.viewToggleBtn,
                    ...(viewMode === 'grid' ? styles.viewToggleBtnActive : {})
                  }}
                  title="Grid Cards View"
                >
                  ⊞ Cards
                </button>
                <button
                  onClick={() => setViewMode('table')}
                  style={{
                    ...styles.viewToggleBtn,
                    ...(viewMode === 'table' ? styles.viewToggleBtnActive : {})
                  }}
                  title="Table Roster View"
                >
                  ☰ Table
                </button>
              </div>
            </div>

            {/* Modal Overlay / Inline Form if editing */}
            {editingEmployee && (
              <div style={styles.modalBackdrop}>
                <div style={styles.modalCardEnhanced}>
                  <div style={styles.modalHeader}>
                    <div>
                      <h3 style={styles.modalTitle}>Update Employee Specifications & Authority</h3>
                      <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#64748b' }}>
                        Configure designated project role, responsibilities, and certified interviewer tier.
                      </p>
                    </div>
                    <button style={styles.modalCloseBtn} onClick={() => setEditingEmployee(null)}>✕</button>
                  </div>
                  <form onSubmit={handleUpdateRoleSubmit} style={styles.modalForm}>
                    {/* Employee Mini Card Header */}
                    <div style={styles.modalEmpHeader}>
                      <div style={{
                        ...styles.roleAvatar,
                        background: getAuthorityMeta(authorityInput).avatarBg
                      }}>
                        {editingEmployee.full_name ? editingEmployee.full_name.charAt(0).toUpperCase() : 'E'}
                      </div>
                      <div>
                        <div style={{ fontWeight: '700', fontSize: '15px', color: '#0f172a' }}>
                          {editingEmployee.full_name}
                        </div>
                        <div style={{ fontSize: '12.5px', color: '#64748b' }}>
                          {editingEmployee.email}
                        </div>
                      </div>
                    </div>

                    {/* Designated Role & Presets */}
                    <div style={styles.modalFormGroup}>
                      <label style={styles.modalLabel}>
                        Designated Project Role *
                      </label>
                      <input 
                        type="text" 
                        value={roleInput} 
                        onChange={(e) => setRoleInput(e.target.value)} 
                        style={styles.modalInput} 
                        required 
                        placeholder="e.g. Senior Backend Engineer"
                      />
                      <div style={styles.presetsRow}>
                        <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '600' }}>Presets:</span>
                        {rolePresets.map(role => (
                          <button
                            key={role}
                            type="button"
                            onClick={() => setRoleInput(role)}
                            style={styles.presetChip}
                          >
                            {role}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Interviewer Authority Delegation Section */}
                    <div style={styles.modalFormGroup}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <label style={styles.modalLabel}>
                          Interviewer Authority Certification *
                        </label>
                        <span style={{ fontSize: '11px', color: '#4f46e5', fontWeight: '700' }}>
                          ⚡ HR Scheduler Integration
                        </span>
                      </div>
                      <div style={styles.authorityCardsGrid}>
                        {[
                          {
                            level: 'NONE',
                            icon: '🛡️',
                            title: 'Exempt (Non-Interviewer)',
                            desc: 'Exempt from candidate evaluations. 100% focused on active project delivery.'
                          },
                          {
                            level: 'L1',
                            icon: '⚡',
                            title: 'L1 Technical Evaluator',
                            desc: 'Certified for Round 1 technical screening, coding assessments, and language syntax.'
                          },
                          {
                            level: 'L2',
                            icon: '🚀',
                            title: 'L2 Domain Specialist',
                            desc: 'Certified for Round 2 architecture, framework internals, system design, and database modeling.'
                          },
                          {
                            level: 'L3',
                            icon: '👑',
                            title: 'L3 Bar Raiser & Managerial',
                            desc: 'Certified for Round 3 managerial rounds, cultural fit, leadership evaluation, and final hire sign-off.'
                          }
                        ].map(tier => {
                          const isSelected = authorityInput === tier.level;
                          return (
                            <div
                              key={tier.level}
                              onClick={() => setAuthorityInput(tier.level)}
                              style={{
                                ...styles.authorityCardOption,
                                ...(isSelected ? styles.authorityCardOptionSelected : {})
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontSize: '18px' }}>{tier.icon}</span>
                                <div style={{ fontWeight: '700', fontSize: '13px', color: isSelected ? '#1e1b4b' : '#0f172a' }}>
                                  {tier.title}
                                </div>
                              </div>
                              <p style={{ margin: '4px 0 0', fontSize: '11.5px', color: '#64748b', lineHeight: 1.4 }}>
                                {tier.desc}
                              </p>
                              {isSelected && (
                                <span style={styles.authoritySelectedCheck}>✓</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Key Responsibilities */}
                    <div style={styles.modalFormGroup}>
                      <label style={styles.modalLabel}>Responsibilities *</label>
                      <textarea 
                        value={respInput} 
                        onChange={(e) => setRespInput(e.target.value)} 
                        style={styles.modalTextarea} 
                        required 
                        rows={4}
                        placeholder="Detail key engineering tasks, technical expectations, and sprint scopes..."
                      />
                    </div>

                    <div style={styles.modalFooter}>
                      <button 
                        type="button" 
                        style={styles.modalBtnCancel} 
                        onClick={() => setEditingEmployee(null)}
                      >
                        Cancel
                      </button>
                      <button 
                        type="submit" 
                        style={styles.modalBtnSave} 
                        disabled={isUpdating}
                      >
                        {isUpdating ? 'Saving...' : 'Save Specifications & Authority'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* RENDER VIEW: Grid Cards or Table Roster */}
            {filteredEmployees.length === 0 ? (
              <div style={styles.tableCard}>
                <div style={{ textAlign: 'center', padding: '40px 20px', color: '#64748b' }}>
                  <div style={{ fontSize: '32px', marginBottom: '8px' }}>🔍</div>
                  <div style={{ fontWeight: '700', fontSize: '16px', color: '#0f172a' }}>No matching team members found</div>
                  <div style={{ fontSize: '13px', marginTop: '4px' }}>Try adjusting your search query or authority filter.</div>
                </div>
              </div>
            ) : viewMode === 'table' ? (
              /* TABLE VIEW */
              <div style={styles.tableCard}>
                <div style={{ overflowX: 'auto' }}>
                  <table style={styles.table}>
                    <thead>
                      <tr style={styles.trHeader}>
                        <th style={styles.th}>Employee Name & Email</th>
                        <th style={styles.th}>Designated Role</th>
                        <th style={styles.th}>Interviewer Authority Tier</th>
                        <th style={styles.th}>Assessor Load (Active / Done)</th>
                        <th style={styles.th}>Bandwidth Status</th>
                        <th style={{ ...styles.th, textAlign: 'right' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredEmployees.map(emp => {
                        const meta = getAuthorityMeta(emp.interviewer_level);
                        return (
                          <tr key={emp.id} style={styles.tr}>
                            <td style={styles.tdName}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div style={{
                                  ...styles.tableAvatar,
                                  background: meta.avatarBg
                                }}>
                                  {emp.full_name ? emp.full_name.charAt(0).toUpperCase() : 'E'}
                                </div>
                                <div>
                                  <div style={{ fontWeight: '700', fontSize: '13.5px', color: '#0f172a' }}>{emp.full_name}</div>
                                  <div style={{ fontSize: '11px', color: '#64748b' }}>{emp.email}</div>
                                </div>
                              </div>
                            </td>
                            <td style={styles.td}>
                              <span style={styles.tableRolePill}>
                                💼 {emp.role || 'Software Engineer'}
                              </span>
                            </td>
                            <td style={styles.td}>
                              <select
                                value={emp.interviewer_level || 'NONE'}
                                disabled={quickUpdatingId === emp.id}
                                onChange={(e) => handleQuickAuthorityChange(emp, e.target.value)}
                                style={{
                                  ...styles.quickAuthoritySelect,
                                  backgroundColor: meta.badgeBg,
                                  color: meta.badgeColor,
                                  borderColor: meta.badgeBorder,
                                  fontWeight: '700'
                                }}
                              >
                                <option value="NONE">🛡️ Exempt (Non-Interviewer)</option>
                                <option value="L1">⚡ L1 Technical Evaluator</option>
                                <option value="L2">🚀 L2 Domain Specialist</option>
                                <option value="L3">👑 L3 Bar Raiser & Lead</option>
                              </select>
                            </td>
                            <td style={styles.td}>
                              <div style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>
                                <strong>{emp.scheduled_count || 0}</strong> Active / <strong>{emp.completed_count || 0}</strong> Done
                              </div>
                            </td>
                            <td style={styles.td}>
                              {getWorkloadChip(emp)}
                            </td>
                            <td style={{ ...styles.td, textAlign: 'right' }}>
                              <button
                                style={styles.btnRoleEdit}
                                onClick={() => handleEditRoleClick(emp)}
                              >
                                ✏️ Update
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              /* GRID CARDS VIEW */
              <div style={styles.rolesGrid}>
                {filteredEmployees.map((emp) => {
                  const meta = getAuthorityMeta(emp.interviewer_level);
                  return (
                    <div style={styles.roleCardModern} key={emp.id}>
                      {/* Top Header */}
                      <div style={styles.roleCardHeaderModern}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                          <div style={{
                            ...styles.roleAvatarModern,
                            background: meta.avatarBg
                          }}>
                            {emp.full_name ? emp.full_name.charAt(0).toUpperCase() : 'E'}
                            <span style={styles.onlineDot} />
                          </div>
                          <div>
                            <h4 style={styles.roleCardNameModern}>{emp.full_name}</h4>
                            <p style={styles.roleCardEmailModern}>{emp.email}</p>
                          </div>
                        </div>
                        <div>
                          {getWorkloadChip(emp)}
                        </div>
                      </div>

                      {/* Card Body */}
                      <div style={styles.roleCardBodyModern}>
                        {/* Designated Role Tag */}
                        <div style={styles.roleDetailGroup}>
                          <span style={styles.roleBadgeLabel}>DESIGNATED ROLE</span>
                          <div style={styles.rolePillTag}>
                            💼 {emp.role || 'Software Engineer'}
                          </div>
                        </div>

                        {/* Interviewer Authority Banner */}
                        <div style={{
                          ...styles.authorityBannerBox,
                          backgroundColor: meta.badgeBg,
                          borderColor: meta.badgeBorder
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '10px', fontWeight: '800', color: meta.badgeColor, letterSpacing: '0.05em' }}>
                              INTERVIEWER AUTHORITY
                            </span>
                            <span style={{ fontSize: '10.5px', fontWeight: '800', color: meta.badgeColor }}>
                              {meta.icon} {meta.label}
                            </span>
                          </div>
                          <div style={{ fontSize: '12px', color: '#1e293b', fontWeight: '600', marginTop: '2px' }}>
                            {meta.fullTitle}
                          </div>
                          <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>
                            {emp.scheduled_count || 0} active booked • {emp.completed_count || 0} evaluations completed
                          </div>
                        </div>
                        
                        {/* Responsibilities */}
                        <div style={styles.roleDetailGroup}>
                          <span style={styles.roleBadgeLabel}>RESPONSIBILITIES & SCOPE</span>
                          <p style={styles.roleValueDescModern}>
                            {emp.responsibilities || 'Core product engineering and task execution'}
                          </p>
                        </div>
                      </div>

                      {/* Card Footer: Quick Authority + Update Button */}
                      <div style={styles.roleCardFooterModern}>
                        <div style={styles.quickAuthorityWrap}>
                          <label style={styles.quickAuthorityLabel}>Authority:</label>
                          <select
                            value={emp.interviewer_level || 'NONE'}
                            disabled={quickUpdatingId === emp.id}
                            onChange={(e) => handleQuickAuthorityChange(emp, e.target.value)}
                            style={{
                              ...styles.quickAuthoritySelect,
                              backgroundColor: meta.badgeBg,
                              color: meta.badgeColor,
                              borderColor: meta.badgeBorder
                            }}
                            title="Quickly change interviewer authority tier"
                          >
                            <option value="NONE">🛡️ Exempt</option>
                            <option value="L1">⚡ L1 Tech</option>
                            <option value="L2">🚀 L2 Domain</option>
                            <option value="L3">👑 L3 Lead</option>
                          </select>
                        </div>

                        <button 
                          style={styles.btnRoleEdit} 
                          onClick={() => handleEditRoleClick(emp)}
                          title="Edit designated role & detailed specifications"
                        >
                          ✏️ Update
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      }
      case 'requisitions':
        return <ManagerRequisitions project={projectData?.project} />;
      case 'exits':
        return (
          <ManagerExits 
            employees={projectData?.employees || []} 
            onSuccess={fetchProjectDetails} 
          />
        );
      case 'onboard':
        return <DataImport onSuccess={fetchProjectDetails} />;
      default:
        return null;
    }
  };

  return (
    <div style={styles.wrapper}>
      <Navbar 
        isLoggedIn={true} 
        userName={managerUser?.full_name} 
        activeTab={activeTab} 
      />
      <div style={styles.dashboardContainer}>
        {/* SIDEBAR */}
        <aside style={styles.sidebar}>
          <div style={styles.userCard}>
            <div style={styles.avatar}>
              {managerUser?.full_name ? managerUser.full_name.charAt(0).toUpperCase() : 'M'}
            </div>
            <div style={styles.userInfo}>
              <h4 style={styles.userName}>{managerUser?.full_name || 'Project Manager'}</h4>
              <p style={styles.userOrg}>{managerUser?.org_name || 'Organization'}</p>
              <span style={styles.roleBadge}>PROJECT MANAGER</span>
            </div>
          </div>

          <nav style={styles.navMenu}>
            {tabs.map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  style={{
                    ...styles.navItem,
                    ...(isActive ? styles.navItemActive : {})
                  }}
                >
                  <span style={{
                    ...styles.navIconWrapper,
                    ...(isActive ? styles.navIconWrapperActive : {})
                  }}>
                    {tab.icon}
                  </span>
                  <span style={styles.navLabel}>{tab.label}</span>
                </button>
              );
            })}
          </nav>

          <div style={styles.sidebarFooter}>
            <button onClick={handleLogout} style={styles.logoutBtn}>
              <svg style={styles.logoutIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Sign Out
            </button>
          </div>
        </aside>

        {/* MAIN PANEL */}
        <main style={styles.mainContent}>
          {/* TOAST NOTIFICATION */}
          {toastMessage && (
            <div style={{
              ...styles.toastContainer,
              backgroundColor: toastMessage.type === 'success' ? '#ecfdf5' : '#fef2f2',
              borderColor: toastMessage.type === 'success' ? '#a7f3d0' : '#fecaca',
              color: toastMessage.type === 'success' ? '#065f46' : '#991b1b'
            }}>
              <span style={{ fontSize: '16px' }}>{toastMessage.type === 'success' ? '✓' : '⚠️'}</span>
              <span style={{ flex: 1, fontSize: '13.5px', fontWeight: '600' }}>{toastMessage.text}</span>
              <button
                onClick={() => setToastMessage(null)}
                style={styles.toastCloseBtn}
              >
                ✕
              </button>
            </div>
          )}
          {renderContent()}
        </main>
      </div>
    </div>
  );
}

// Styling system using CSS variables matching HRDashboard
const styles = {
  wrapper: {
    minHeight: '100vh',
    backgroundColor: 'var(--bg-page, #f8fafc)',
    color: 'var(--text-main, #0f172a)',
    display: 'flex',
    flexDirection: 'column'
  },
  dashboardContainer: {
    flex: 1,
    display: 'flex',
    maxWidth: '1600px',
    margin: '0 auto',
    width: '100%',
    padding: 'clamp(16px, 1.8vw, 24px) clamp(14px, 1.5vw, 20px)',
    gap: 'clamp(16px, 1.8vw, 24px)',
    boxSizing: 'border-box'
  },
  sidebar: {
    width: 'clamp(210px, 16vw, 245px)',
    flexShrink: 0,
    background: 'var(--bg-card-glass)',
    backdropFilter: 'blur(16px)',
    border: '1px solid var(--border-light)',
    borderRadius: '16px',
    padding: '12px 14px',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: 'var(--shadow-md)',
    boxSizing: 'border-box',
    height: 'calc(100vh - 84px)',
    position: 'sticky',
    top: '70px'
  },
  userCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    paddingBottom: '12px',
    borderBottom: '1px solid var(--border-color)',
    marginBottom: '12px'
  },
  avatar: {
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    background: 'linear-gradient(135deg, var(--primary-color) 0%, #3b82f6 100%)',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '15px',
    fontWeight: '800',
    boxShadow: '0 3px 8px rgba(79, 70, 229, 0.25)'
  },
  userInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1px',
    overflow: 'hidden'
  },
  userName: {
    margin: 0,
    fontSize: '13.5px',
    fontWeight: '700',
    color: 'var(--text-primary)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  userOrg: {
    margin: 0,
    fontSize: '11px',
    color: 'var(--text-muted)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  roleBadge: {
    alignSelf: 'flex-start',
    marginTop: '2px',
    fontSize: '9px',
    fontWeight: '700',
    letterSpacing: '0.8px',
    padding: '1px 6px',
    borderRadius: '4px',
    background: 'var(--accent-color-transparent)',
    color: 'var(--primary-color)',
    border: '1px solid rgba(79, 70, 229, 0.2)'
  },
  navMenu: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    flex: 1,
    overflowY: 'auto',
    paddingRight: '2px',
    marginRight: '-2px'
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 10px',
    borderRadius: '8px',
    border: 'none',
    background: 'transparent',
    color: 'var(--text-secondary)',
    fontSize: '12.5px',
    fontWeight: '500',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    width: '100%',
    boxSizing: 'border-box'
  },
  navItemActive: {
    background: 'var(--bg-hover)',
    color: 'var(--text-primary)',
    fontWeight: '600'
  },
  navIconWrapper: {
    width: '26px',
    height: '26px',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'transparent',
    color: 'var(--text-muted)',
    transition: 'all 0.2s'
  },
  navIconWrapperActive: {
    background: 'linear-gradient(135deg, rgba(79, 70, 229, 0.1) 0%, rgba(59, 130, 246, 0.1) 100%)',
    color: 'var(--primary-color)'
  },
  tabIcon: {
    width: '15px',
    height: '15px'
  },
  navLabel: {
    flex: 1
  },
  sidebarFooter: {
    paddingTop: '10px',
    borderTop: '1px solid var(--border-color)',
    marginTop: '10px'
  },
  logoutBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    width: '100%',
    padding: '8px',
    borderRadius: '8px',
    border: '1px solid var(--border-color)',
    background: 'transparent',
    color: 'var(--text-secondary)',
    cursor: 'pointer',
    fontSize: '12.5px',
    fontWeight: '600',
    transition: 'all 0.2s'
  },
  logoutIcon: {
    width: '14px',
    height: '14px'
  },
  mainContent: {
    flex: 1,
    height: 'calc(100vh - 84px)',
    overflowY: 'auto',
    boxSizing: 'border-box'
  },
  loader: {
    textAlign: 'center',
    padding: '48px',
    color: 'var(--text-muted, #64748b)',
    fontSize: '16px'
  },
  overviewContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px'
  },
  cardHeader: {
    backgroundColor: 'var(--bg-card-glass, rgba(255, 255, 255, 0.8))',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderRadius: '16px',
    padding: '24px',
    backdropFilter: 'blur(10px)'
  },
  sectionTitle: {
    margin: 0,
    fontSize: '24px',
    fontWeight: '700'
  },
  sectionSubtitle: {
    margin: '8px 0 0 0',
    color: 'var(--text-muted, #64748b)',
    fontSize: '14px'
  },
  statsRow: {
    display: 'flex',
    gap: '20px'
  },
  statCard: {
    flex: 1,
    backgroundColor: 'var(--bg-card-glass, rgba(255, 255, 255, 0.8))',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderRadius: '16px',
    padding: '24px',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
    display: 'flex',
    alignItems: 'center',
    boxSizing: 'border-box'
  },
  statLabel: {
    margin: 0,
    fontSize: '14px',
    fontWeight: '500',
    color: 'var(--text-muted, #64748b)'
  },
  statValue: {
    margin: '8px 0 0 0',
    fontSize: '36px',
    fontWeight: '800',
    color: 'var(--accent-primary, #4f46e5)'
  },
  tableCard: {
    backgroundColor: 'var(--bg-card-glass, rgba(255, 255, 255, 0.8))',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderRadius: '16px',
    padding: '24px',
    backdropFilter: 'blur(10px)'
  },
  tableTitle: {
    margin: '0 0 20px 0',
    fontSize: '18px',
    fontWeight: '600'
  },
  emptyText: {
    color: 'var(--text-muted, #64748b)',
    textAlign: 'center',
    padding: '24px'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    textAlign: 'left'
  },
  trHeader: {
    borderBottom: '2px solid var(--border-color, #e2e8f0)'
  },
  th: {
    padding: '12px 16px',
    color: 'var(--text-muted, #64748b)',
    fontWeight: '600',
    fontSize: '13px'
  },
  tr: {
    borderBottom: '1px solid var(--border-color, #e2e8f0)'
  },
  tdName: {
    padding: '16px',
    fontWeight: '600'
  },
  td: {
    padding: '16px',
    fontSize: '14px'
  },
  badgeActive: {
    backgroundColor: '#dcfce7',
    color: '#15803d',
    padding: '4px 10px',
    borderRadius: '99px',
    fontSize: '11px',
    fontWeight: 'bold',
    display: 'inline-block'
  },
  badgePending: {
    backgroundColor: '#fef3c7',
    color: '#b45309',
    padding: '4px 10px',
    borderRadius: '99px',
    fontSize: '11px',
    fontWeight: 'bold',
    display: 'inline-block'
  },
  badgeApproved: {
    backgroundColor: '#fee2e2',
    color: '#dc2626',
    padding: '4px 10px',
    borderRadius: '99px',
    fontSize: '11px',
    fontWeight: 'bold',
    display: 'inline-block'
  },
  cardHeaderLayout: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  projectActiveBadge: {
    fontSize: '11px',
    fontWeight: '800',
    backgroundColor: '#dcfce7',
    color: '#15803d',
    padding: '4px 12px',
    borderRadius: '99px',
    letterSpacing: '0.05em'
  },
  projectPendingBadge: {
    fontSize: '11px',
    fontWeight: '800',
    backgroundColor: 'rgba(234, 179, 8, 0.15)',
    color: '#b45309',
    padding: '4px 12px',
    borderRadius: '99px',
    letterSpacing: '0.05em'
  },
  infoBanner: {
    marginBottom: '20px',
    padding: '16px 20px',
    backgroundColor: 'var(--bg-card-glass, rgba(255, 255, 255, 0.8))',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderRadius: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    flexWrap: 'wrap'
  },
  btnBannerAction: {
    backgroundColor: 'var(--accent-primary, #4f46e5)',
    color: '#ffffff',
    border: 'none',
    borderRadius: '10px',
    padding: '8px 18px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(79, 70, 229, 0.2)'
  },
  statIconContainerIndigo: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    backgroundColor: 'rgba(79, 70, 229, 0.1)',
    color: 'var(--accent-primary, #4f46e5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '16px'
  },
  statIconContainerRed: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    color: '#ef4444',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '16px'
  },
  statIconContainerTeal: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    backgroundColor: 'rgba(6, 182, 212, 0.1)',
    color: '#06b6d4',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '16px'
  },
  statIcon: {
    width: '24px',
    height: '24px',
    color: 'currentColor'
  },
  statValueRed: {
    margin: '8px 0 0 0',
    fontSize: '36px',
    fontWeight: '800',
    color: '#ef4444',
    lineHeight: 1
  },
  statValueTeal: {
    margin: '8px 0 0 0',
    fontSize: '36px',
    fontWeight: '800',
    color: '#06b6d4',
    lineHeight: 1
  },
  statSubtext: {
    fontSize: '12px',
    color: 'var(--text-muted, #64748b)',
    marginTop: '4px',
    display: 'inline-block'
  },
  stabilityCard: {
    backgroundColor: 'var(--bg-card-glass, rgba(255, 255, 255, 0.8))',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderRadius: '16px',
    padding: '24px',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  stabilityHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  stabilityTitle: {
    margin: 0,
    fontSize: '18px',
    fontWeight: '600'
  },
  stabilitySubtitle: {
    margin: '4px 0 0 0',
    color: 'var(--text-muted, #64748b)',
    fontSize: '13px'
  },
  stabilityScoreCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end'
  },
  stabilityScore: {
    fontSize: '32px',
    fontWeight: '800',
    color: 'var(--accent-primary, #4f46e5)',
    lineHeight: 1
  },
  stabilityScoreLabel: {
    fontSize: '11px',
    color: 'var(--text-muted, #64748b)',
    marginTop: '4px',
    fontWeight: '500'
  },
  stabilityBarBg: {
    height: '10px',
    width: '100%',
    backgroundColor: '#f1f5f9',
    borderRadius: '99px',
    overflow: 'hidden',
    display: 'flex'
  },
  stabilityBarFillActive: {
    height: '100%',
    backgroundColor: 'var(--accent-primary, #4f46e5)',
    borderRadius: '99px 0 0 99px',
    transition: 'width 0.4s ease-out'
  },
  stabilityBarFillExit: {
    height: '100%',
    backgroundColor: '#f59e0b',
    borderRadius: '0 99px 99px 0',
    transition: 'width 0.4s ease-out'
  },
  stabilityLegend: {
    display: 'flex',
    gap: '24px'
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    color: 'var(--text-muted, #64748b)'
  },
  legendDotActive: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: 'var(--accent-primary, #4f46e5)'
  },
  legendDotExit: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: '#f59e0b'
  },
  rosterGrid: {
    display: 'flex',
    gap: '24px',
    flexWrap: 'wrap',
    width: '100%'
  },
  tableCardHeaderBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    marginBottom: '20px'
  },
  tableSubtitle: {
    margin: 0,
    fontSize: '13px',
    color: 'var(--text-muted, #64748b)'
  },
  tdTitle: {
    padding: '16px',
    fontWeight: '600'
  },
  rolesGrid: {
    display: 'flex',
    gap: '24px',
    flexWrap: 'wrap',
    width: '100%',
    boxSizing: 'border-box'
  },
  roleCard: {
    flex: '1 1 calc(33.333% - 16px)',
    minWidth: '300px',
    backgroundColor: 'var(--bg-card-glass, rgba(255, 255, 255, 0.8))',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderRadius: '16px',
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
    boxSizing: 'border-box'
  },
  roleCardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    borderBottom: '1px solid var(--border-color, #e2e8f0)',
    paddingBottom: '16px'
  },
  roleAvatar: {
    width: '44px',
    height: '44px',
    borderRadius: '10px',
    backgroundColor: 'var(--accent-primary, #4f46e5)',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    fontWeight: 'bold'
  },
  roleCardName: {
    margin: 0,
    fontSize: '15px',
    fontWeight: '700',
    color: 'var(--text-main, #0f172a)'
  },
  roleCardEmail: {
    margin: '2px 0 0 0',
    fontSize: '12px',
    color: 'var(--text-muted, #64748b)'
  },
  roleCardBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    flex: 1
  },
  roleDetailGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  },
  roleBadgeLabel: {
    alignSelf: 'flex-start',
    fontSize: '9px',
    fontWeight: '800',
    color: 'var(--text-muted, #64748b)',
    letterSpacing: '0.05em'
  },
  roleValueText: {
    fontSize: '14px',
    fontWeight: '600',
    color: 'var(--accent-primary, #4f46e5)'
  },
  roleValueDesc: {
    margin: 0,
    fontSize: '13px',
    color: 'var(--text-main, #334155)',
    lineHeight: 1.5
  },
  roleCardFooter: {
    borderTop: '1px solid var(--border-color, #e2e8f0)',
    paddingTop: '16px',
    display: 'flex',
    justifyContent: 'flex-end'
  },
  btnRoleEdit: {
    backgroundColor: 'transparent',
    color: 'var(--accent-primary, #4f46e5)',
    border: '1px solid var(--accent-primary, #4f46e5)',
    padding: '7px 14px',
    borderRadius: '8px',
    fontSize: '12px',
    fontWeight: '700',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    whiteSpace: 'nowrap'
  },
  controlsBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '14px',
    flexWrap: 'wrap',
    marginBottom: '20px',
    backgroundColor: 'var(--bg-card-glass, rgba(255, 255, 255, 0.85))',
    padding: '12px 18px',
    borderRadius: '14px',
    border: '1px solid var(--border-color, #e2e8f0)',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)'
  },
  searchBoxWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '10px',
    padding: '0 12px',
    flex: '1 1 260px',
    minWidth: '200px',
    maxWidth: '380px'
  },
  searchIconSymbol: {
    fontSize: '14px',
    color: '#94a3b8'
  },
  searchInputField: {
    border: 'none',
    outline: 'none',
    background: 'transparent',
    padding: '9px 0',
    fontSize: '13px',
    color: '#0f172a',
    width: '100%'
  },
  clearSearchBtn: {
    background: 'none',
    border: 'none',
    color: '#94a3b8',
    cursor: 'pointer',
    fontSize: '12px',
    padding: '4px'
  },
  filterPillsGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    flexWrap: 'wrap'
  },
  filterPillBtn: {
    padding: '6px 12px',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    backgroundColor: '#ffffff',
    color: '#64748b',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.15s ease'
  },
  filterPillBtnActive: {
    backgroundColor: 'var(--accent-primary, #4f46e5)',
    borderColor: 'var(--accent-primary, #4f46e5)',
    color: '#ffffff',
    boxShadow: '0 2px 6px rgba(79, 70, 229, 0.25)'
  },
  viewModeToggleGroup: {
    display: 'flex',
    gap: '4px',
    backgroundColor: '#f1f5f9',
    padding: '4px',
    borderRadius: '8px'
  },
  viewToggleBtn: {
    padding: '5px 12px',
    borderRadius: '6px',
    border: 'none',
    backgroundColor: 'transparent',
    color: '#64748b',
    fontSize: '12px',
    fontWeight: '700',
    cursor: 'pointer',
    transition: 'all 0.15s ease'
  },
  viewToggleBtnActive: {
    backgroundColor: '#ffffff',
    color: '#0f172a',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)'
  },
  roleCardModern: {
    flex: '1 1 calc(33.333% - 16px)',
    minWidth: '310px',
    backgroundColor: 'var(--bg-card-glass, rgba(255, 255, 255, 0.95))',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderRadius: '16px',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 4px 10px -2px rgba(0, 0, 0, 0.04)',
    boxSizing: 'border-box',
    transition: 'transform 0.15s ease, box-shadow 0.15s ease'
  },
  roleCardHeaderModern: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '12px',
    borderBottom: '1px solid #f1f5f9',
    paddingBottom: '14px'
  },
  roleAvatarModern: {
    width: '44px',
    height: '44px',
    borderRadius: '12px',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    fontWeight: 'bold',
    position: 'relative',
    flexShrink: 0,
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)'
  },
  onlineDot: {
    position: 'absolute',
    bottom: '-2px',
    right: '-2px',
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: '#22c55e',
    border: '2px solid #ffffff'
  },
  roleCardNameModern: {
    margin: 0,
    fontSize: '15px',
    fontWeight: '700',
    color: 'var(--text-main, #0f172a)'
  },
  roleCardEmailModern: {
    margin: '2px 0 0 0',
    fontSize: '12px',
    color: 'var(--text-muted, #64748b)',
    wordBreak: 'break-all'
  },
  roleCardBodyModern: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    flex: 1
  },
  rolePillTag: {
    alignSelf: 'flex-start',
    fontSize: '13px',
    fontWeight: '700',
    color: '#1e293b',
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    padding: '4px 10px'
  },
  authorityBannerBox: {
    borderRadius: '10px',
    border: '1px solid',
    padding: '10px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  },
  roleValueDescModern: {
    margin: 0,
    fontSize: '12.5px',
    color: 'var(--text-main, #475569)',
    lineHeight: 1.4,
    backgroundColor: '#f8fafc',
    padding: '8px 10px',
    borderRadius: '8px',
    border: '1px dashed #e2e8f0'
  },
  roleCardFooterModern: {
    borderTop: '1px solid #f1f5f9',
    paddingTop: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '10px'
  },
  quickAuthorityWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    flex: 1,
    minWidth: 0
  },
  quickAuthorityLabel: {
    fontSize: '11px',
    fontWeight: '700',
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    flexShrink: 0
  },
  quickAuthoritySelect: {
    padding: '5px 8px',
    borderRadius: '8px',
    border: '1px solid #cbd5e1',
    fontSize: '12px',
    fontWeight: '600',
    outline: 'none',
    cursor: 'pointer',
    backgroundColor: '#ffffff',
    color: '#0f172a',
    width: '100%',
    minWidth: '110px'
  },
  tableAvatar: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: 'bold',
    flexShrink: 0
  },
  tableRolePill: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#334155',
    backgroundColor: '#f1f5f9',
    padding: '3px 8px',
    borderRadius: '6px',
    display: 'inline-block'
  },
  modalCardEnhanced: {
    backgroundColor: '#ffffff',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderRadius: '18px',
    width: '100%',
    maxWidth: '580px',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    display: 'flex',
    flexDirection: 'column'
  },
  modalEmpHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    backgroundColor: '#f8fafc',
    borderRadius: '12px',
    border: '1px solid #e2e8f0'
  },
  presetsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    flexWrap: 'wrap',
    marginTop: '6px'
  },
  presetChip: {
    background: '#f1f5f9',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    padding: '2px 8px',
    fontSize: '11px',
    fontWeight: '600',
    color: '#475569',
    cursor: 'pointer',
    transition: 'all 0.15s ease'
  },
  authorityCardsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
    gap: '10px',
    marginTop: '4px'
  },
  authorityCardOption: {
    position: 'relative',
    border: '1.5px solid #e2e8f0',
    borderRadius: '12px',
    padding: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    backgroundColor: '#ffffff'
  },
  authorityCardOptionSelected: {
    borderColor: '#4f46e5',
    backgroundColor: 'rgba(79, 70, 229, 0.04)',
    boxShadow: '0 0 0 1px #4f46e5'
  },
  authoritySelectedCheck: {
    position: 'absolute',
    top: '10px',
    right: '10px',
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    backgroundColor: '#4f46e5',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '11px',
    fontWeight: 'bold'
  },
  toastContainer: {
    position: 'fixed',
    top: '24px',
    right: '24px',
    zIndex: 99999,
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 18px',
    borderRadius: '10px',
    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.15)',
    border: '1px solid',
    maxWidth: '420px',
    animation: 'fadeIn 0.2s ease-in-out'
  },
  toastCloseBtn: {
    background: 'none',
    border: 'none',
    color: 'currentColor',
    cursor: 'pointer',
    fontSize: '14px',
    marginLeft: '6px',
    padding: '0 4px'
  },
  modalBackdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.4)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '20px'
  },
  modalCard: {
    backgroundColor: '#ffffff',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '480px',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  },
  modalHeader: {
    padding: '16px 24px',
    borderBottom: '1px solid var(--border-color, #e2e8f0)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  modalTitle: {
    margin: 0,
    fontSize: '16px',
    fontWeight: '700',
    color: '#0f172a'
  },
  modalCloseBtn: {
    background: 'none',
    border: 'none',
    fontSize: '18px',
    color: '#64748b',
    cursor: 'pointer'
  },
  modalForm: {
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  modalFormGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  },
  modalLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#64748b'
  },
  modalInput: {
    padding: '10px 14px',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    fontSize: '14px',
    color: '#0f172a',
    outline: 'none',
    transition: 'border-color 0.2s ease'
  },
  modalInputDisabled: {
    padding: '10px 14px',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    fontSize: '14px',
    color: '#64748b',
    backgroundColor: '#f8fafc',
    cursor: 'not-allowed'
  },
  modalTextarea: {
    padding: '10px 14px',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    fontSize: '14px',
    color: '#0f172a',
    outline: 'none',
    transition: 'border-color 0.2s ease',
    resize: 'vertical'
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    marginTop: '8px'
  },
  modalBtnCancel: {
    backgroundColor: 'transparent',
    color: '#64748b',
    border: '1px solid #e2e8f0',
    padding: '10px 20px',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer'
  },
  modalBtnSave: {
    backgroundColor: 'var(--accent-primary, #4f46e5)',
    color: '#ffffff',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 4px 6px -1px rgba(79, 70, 229, 0.2)'
  },
  funnelCard: {
    background: 'var(--bg-card-glass, rgba(255, 255, 255, 0.8))',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderRadius: '16px',
    padding: '24px',
    backdropFilter: 'blur(10px)',
    marginTop: '24px'
  },
  funnelStages: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '8px',
    marginTop: '20px',
    flexWrap: 'wrap'
  },
  funnelStage: {
    flex: 1,
    minWidth: '110px',
    background: 'var(--bg-page, #f8fafc)',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderRadius: '12px',
    padding: '16px',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '6px'
  },
  funnelArrow: {
    fontSize: '20px',
    color: 'var(--text-muted, #94a3b8)',
    fontWeight: 'bold',
    userSelect: 'none'
  },
  funnelStageVal: {
    fontSize: '24px',
    fontWeight: '800',
    color: 'var(--text-main, #0f172a)'
  },
  funnelStageLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: 'var(--text-muted, #64748b)'
  },
  funnelIconBlue: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    backgroundColor: 'rgba(59, 130, 246, 0.1)',
    color: '#3b82f6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px'
  },
  funnelIconPurple: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    backgroundColor: 'rgba(139, 92, 246, 0.1)',
    color: '#8b5cf6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px'
  },
  funnelIconYellow: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    backgroundColor: 'rgba(245, 158, 11, 0.1)',
    color: '#f59e0b',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px'
  },
  funnelIconTeal: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    backgroundColor: 'rgba(20, 184, 166, 0.1)',
    color: '#14b8a6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px'
  },
  funnelIconGreen: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    backgroundColor: 'rgba(16, 185, 129, 0.1)',
    color: '#10b981',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px'
  }
};
